import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EnviarInformeDialog } from '@/features/envios/components/EnviarInformeDialog';
import { profesionalesApi } from '@/features/profesionales/api';
import { profesionalesKeys } from '@/features/profesionales/hooks/useProfesionales';
import { useNotify } from '@/hooks/useNotify';

import type { Protocolo } from '../types';
import { ProtocoloKebabMenu } from './ProtocoloKebabMenu';

interface ProtocoloListRowActionsProps {
  protocolo: Protocolo;
  canManage: boolean;
  getEstablecimientoName: (uid: string) => string;
  hideEnviar?: boolean;
}

export function ProtocoloListRowActions({
  protocolo,
  canManage,
  getEstablecimientoName,
  hideEnviar = false,
}: ProtocoloListRowActionsProps): JSX.Element {
  const notify = useNotify();
  const [enviarDialogOpen, setEnviarDialogOpen] = useState(false);
  const [kebabAnchor, setKebabAnchor] = useState<HTMLElement | null>(null);

  // enabled: false — no dispara al montar la fila, solo on-demand con refetch()
  // TODO: confirmar disponibilidad de cantidadAnalisis en response del listado
  // para deshabilitar el botón cuando el protocolo no tiene análisis cargados.
  const profesionalQuery = useQuery({
    queryKey: profesionalesKeys.detail(protocolo.uid_profesional),
    queryFn: ({ signal }) => profesionalesApi.getById(protocolo.uid_profesional, signal),
    enabled: false,
  });

  async function handleEnviarClick(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    if (profesionalQuery.data) {
      setEnviarDialogOpen(true);
      return;
    }
    const result = await profesionalQuery.refetch();
    if (result.status === 'success') {
      setEnviarDialogOpen(true);
    } else {
      notify.error('No se pudo cargar el profesional. Intentá de nuevo.');
    }
  }

  function handleKebabClick(e: React.MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    setKebabAnchor(e.currentTarget);
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
        {canManage && !hideEnviar && (
          <Tooltip title="Enviar informe">
            <span>
              <IconButton
                size="small"
                disabled={profesionalQuery.isFetching}
                onClick={(e) => void handleEnviarClick(e)}
              >
                {profesionalQuery.isFetching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton size="small" onClick={handleKebabClick}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <ProtocoloKebabMenu
        protocolo={protocolo}
        canManage={canManage}
        anchorEl={kebabAnchor}
        onClose={() => setKebabAnchor(null)}
      />

      {profesionalQuery.data && (
        <EnviarInformeDialog
          open={enviarDialogOpen}
          onClose={() => setEnviarDialogOpen(false)}
          uidProtocolo={protocolo.uid_protocolo}
          finalYaEmitido={protocolo.informe_final_emitido}
          numeroProtocolo={protocolo.numero_protocolo}
          establecimientoNombre={getEstablecimientoName(protocolo.uid_establecimiento)}
          profesional={profesionalQuery.data}
        />
      )}
    </>
  );
}
